// @hssna/family-web 应用外壳
// 规格: SPEC §8.1 family-web 功能规格 / ADR-0012 (Tailwind) / OLD-01 (适老化)
//
// 家属门户外壳: 顶部导航栏 + 侧边栏 + 主内容区 (Outlet)
// 侧边栏导航项对齐 SPEC §8.1 功能模块

import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import type { Role } from '@hssna/ts-shared';
import { useAuthStore } from '../store/auth-store';
import { DeviceStatusBanner } from './DeviceStatusBanner';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: string;
  readonly end?: boolean;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: '首页', icon: '🏠', end: true },
  { to: '/members', label: '家庭成员', icon: '👥' },
  { to: '/devices', label: '设备', icon: '📱' },
  { to: '/privacy', label: '隐私控制台', icon: '🔒' },
  { to: '/consent', label: '授权管理', icon: '✅' },
  { to: '/export', label: '数据导出', icon: '📤' },
  { to: '/alert-center', label: '实时告警中心', icon: '🔔' },
  { to: '/alerts', label: '告警历史', icon: '🚨' },
];

const ROLE_LABELS: Record<Role, string> = {
  family_head: '户主',
  family_member: '家庭成员',
  family_guardian: '家庭监护人',
  practitioner: '医护人员',
  specialist: '专科医生',
  nurse: '护士',
  ops_admin: '运营管理员',
  ops_operator: '运营操作员',
  ems_dispatcher: '急救调度员',
  system_service: '系统服务账号',
};

export function AppLayout() {
  const role = useAuthStore((s) => s.role);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const roleLabel = role ? ROLE_LABELS[role] : '未登录';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* 顶部导航栏 */}
      <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSidebarOpen((v) => !v)}
            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 md:hidden"
            aria-label="切换侧边栏"
          >
            <span className="text-xl">☰</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900">HSSNA 家属门户</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-brand-primary/10 px-3 py-1 text-sm font-medium text-brand-primary">
            {roleLabel}
          </span>
          {user?.username && typeof user.username === 'string' ? (
            <span className="hidden text-sm text-gray-600 sm:inline">{user.username}</span>
          ) : user?.sub ? (
            <span className="hidden text-sm text-gray-600 sm:inline">{user.sub}</span>
          ) : null}
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            退出登录
          </button>
        </div>
      </header>

      {/* T4-08: 设备降级 + 边缘节点断网/恢复 全局横幅 */}
      <DeviceStatusBanner />

      <div className="flex flex-1">
        {/* 侧边栏 */}
        <aside
          className={`${sidebarOpen ? 'block' : 'hidden'} w-56 shrink-0 border-r border-gray-200 bg-white md:block`}
        >
          <nav className="flex flex-col gap-1 p-3">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-primary/10 text-brand-primary'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* 主内容区 */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
